import React, { useEffect, useState } from "react";
import { Box } from "@mui/system";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import RadioButton from "../../../../../components/RadioButton/RadioButton";
import { AutoCompleteCustom } from "../../../../../components/Autocomplete/AutoCompleteCustom";
import { BRAND, GREY, PRIMARY } from "../../../../../utils/constants/colors";
import { ICON_LABELS, fontWeight } from "../../../../../utils/constants/UI";
import { STRINGS } from "../../strings";
import Checkbox from "../../../../../components/Buttons/Checkbox/Checkbox";
import { IconComponent } from "../../../../../components/Icon";

const SelectSequenceStep = ({
  classes,
  sequenceType,
  handleSequenceTypeChange,
  sequenceList,
  sequenceLabel,
  setSequenceLabel,
  sequence,
  setSequence,
  contactList,
  listLabel,
  setListLabel,
  selectedList,
  setSelectedList,
  currentStep,
  totalSteps,
  icpPersonaEnabled,
  setIcpPersonaEnabled,
  companyPersonas,
  personPersonas,
  companyPersonaId,
  setCompanyPersonaId,
  companyPersonaLabel,
  setCompanyPersonaLabel,
  personPersonaId,
  setPersonPersonaId,
  personPersonaLabel,
  setPersonPersonaLabel,
  personasLoading,
  hideSequenceTypeSelector,
}) => {
  const [sequenceItems, setSequenceItems] = useState(sequenceList || []);
  const [contactItems, setContactItems] = useState(contactList || []);
  const [companyPersonaItems, setCompanyPersonaItems] = useState(
    companyPersonas || [],
  );

  useEffect(() => {
    setSequenceItems(sequenceList || []);
  }, [sequenceList]);

  useEffect(() => {
    setContactItems(contactList || []);
  }, [contactList]);

  useEffect(() => {
    setCompanyPersonaItems(companyPersonas || []);
  }, [companyPersonas]);

  const filterBySearch = (source, query) => {
    const q = query?.toLowerCase()?.trim();
    if (!q) return source || [];
    return (source || []).filter((item) =>
      item?.label?.toString()?.toLowerCase()?.includes(q),
    );
  };

  const handleSequenceSearch = (event) => {
    setSequenceItems(filterBySearch(sequenceList, event?.target?.value));
  };

  const handleContactSearch = (event) => {
    setContactItems(filterBySearch(contactList, event?.target?.value));
  };

  const handleCompanyPersonaSearch = (event) => {
    setCompanyPersonaItems(
      filterBySearch(companyPersonas, event?.target?.value),
    );
  };

  return (
    <Box data-tour="campaign-sequence-list-section">
      <div className={classes.div}>
        <TypographyInline
          size={"md"}
          color={GREY.PRIMARY}
          fontWeight={fontWeight.SEMIBOLD}
          label={STRINGS.SELECT_SEQ_LIST}
        />
        <TypographyInline
          size={"sm"}
          color={GREY.SECONDARY}
          fontWeight={fontWeight.REGULAR}
          label={
            currentStep
              ? `Step ${currentStep} of ${totalSteps}`
              : STRINGS.STEP25
          }
        />
      </div>
      <Box className={classes.containStep}>
        {!hideSequenceTypeSelector && <Box className={classes.mTop}>
          <TypographyInline
            size={"sm"}
            color={GREY.QUATINARY}
            fontWeight={fontWeight.MEDIUM}
            label="Sequence Type"
          />
          <Box sx={{ mt: 1, mb: 2 }}>
            <RadioButton
              items={[
                { id: "automated", label: "AI-Powered" },
                { id: "manual", label: "Automated" },
                { id: "tenantPrompt", label: "Custom" },
              ]}
              value={sequenceType}
              onChange={handleSequenceTypeChange}
            />
          </Box>
        </Box>}

        <div className={classes.stepTwoLabelSequence}>
          <TypographyInline
            size={"sm"}
            color={GREY.QUATINARY}
            fontWeight={fontWeight.MEDIUM}
            label={STRINGS.SEQ_TOBE_USED}
          />
        </div>
        <Box className={classes.mTop}>
          <AutoCompleteCustom
            items={sequenceItems}
            LeftIcon=""
            valueState={[sequenceLabel]}
            setValueState={setSequenceLabel}
            idState={[sequence]}
            setIdState={setSequence}
            papermenuclass={classes.paperMenu}
            size={"sm36"}
            isSearchEnable={true}
            handleSearch={handleSequenceSearch}
          />
        </Box>

        <Box data-tour="campaign-contact-list">
          <div className={classes.stepTwoLabelLists}>
            <TypographyInline
              size={"sm"}
              color={GREY.QUATINARY}
              fontWeight={fontWeight.MEDIUM}
              label={STRINGS.LIST_TOBE_USED}
            />
          </div>

          <Box className={classes.mTop}>
            <AutoCompleteCustom
              items={contactItems}
              LeftIcon=""
              valueState={[listLabel]}
              setValueState={setListLabel}
              idState={[selectedList]}
              setIdState={setSelectedList}
              papermenuclass={classes.paperMenu}
              size={"sm36"}
              isSearchEnable={true}
              handleSearch={handleContactSearch}
            />
          </Box>
        </Box>

        {/* ICP Persona Checkbox Section */}
        <Box className={classes.icpPersonaSection} data-tour="campaign-icp-section">
          <div
            className={
              icpPersonaEnabled
                ? classes.icpToggleRowActive
                : classes.icpToggleRow
            }
          >
            <div className={classes.icpToggleLeft}>
              <div className={classes.icpIconWrapper}>
                <IconComponent
                  color={PRIMARY.W_500}
                  fontSize={"16px"}
                  iconLabel={ICON_LABELS.TARGET}
                />
              </div>
              <div className={classes.icpToggleInfo}>
                <TypographyInline
                  size={"sm"}
                  color={GREY.PRIMARY}
                  fontWeight={fontWeight.SEMIBOLD}
                  label={STRINGS.MATCH_ICP_PERSONA}
                />
                <TypographyInline
                  size={"xs"}
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.REGULAR}
                  label={STRINGS.ICP_PERSONA_DESC}
                />
              </div>
            </div>
            <div style={{ transform: "scale(1.35)", flexShrink: 0 }}>
              <Checkbox
                checked={icpPersonaEnabled}
                onChange={() => setIcpPersonaEnabled(!icpPersonaEnabled)}
              />
            </div>
          </div>

          {icpPersonaEnabled && (
            <div className={classes.icpPersonaContent}>
              <div className={classes.icpPersonaDropdowns}>
                <div className={classes.icpPersonaField}>
                  <div className={classes.icpPersonaLabel}>
                    <IconComponent
                      color={GREY.QUATINARY}
                      fontSize={"14px"}
                      iconLabel={ICON_LABELS.BUSINESS}
                    />
                    <TypographyInline
                      size={"xs"}
                      color={GREY.QUATINARY}
                      fontWeight={fontWeight.MEDIUM}
                      label={STRINGS.COMPANY_PERSONA}
                    />
                  </div>
                  <AutoCompleteCustom
                    items={companyPersonaItems}
                    LeftIcon=""
                    valueState={[companyPersonaLabel]}
                    setValueState={setCompanyPersonaLabel}
                    idState={[companyPersonaId]}
                    setIdState={setCompanyPersonaId}
                    papermenuclass={classes.paperMenu}
                    size={"sm36"}
                    paperHeight={"120px"}
                    placeholder={STRINGS.SELECT_COMPANY_PERSONA}
                    isLoading={personasLoading}
                    isSearchEnable={true}
                    handleSearch={handleCompanyPersonaSearch}
                  />
                </div>
                {/* Person persona hidden */}
                {/* <div className={classes.icpPersonaField}>
                  <div className={classes.icpPersonaLabel}>
                    <IconComponent
                      color={GREY.QUATINARY}
                      fontSize={"14px"}
                      iconLabel={ICON_LABELS.PERSON}
                    />
                    <TypographyInline
                      size={"xs"}
                      color={GREY.QUATINARY}
                      fontWeight={fontWeight.MEDIUM}
                      label={STRINGS.PERSON_PERSONA}
                    />
                  </div>
                  <AutoCompleteCustom
                    items={personPersonas}
                    LeftIcon=""
                    valueState={[personPersonaLabel]}
                    setValueState={setPersonPersonaLabel}
                    idState={[personPersonaId]}
                    setIdState={setPersonPersonaId}
                    papermenuclass={classes.paperMenu}
                    size={"sm36"}
                    paperHeight={"120px"}
                    placeholder={STRINGS.SELECT_PERSON_PERSONA}
                    isLoading={personasLoading}
                  />
                </div> */}
              </div>
            </div>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(SelectSequenceStep);
